import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { tenantService } from '../../services/tenant.service';
import { mediaService } from '../../services/studio.service';
import { videoService } from '../../services/video.service';
import { trackAppCreation } from '../../components/analytics/GoogleAnalytics';
import { startBoot, clearBoot, type BootCopy, type BootKind } from './BootOverlay';
import type { ComposerCardProps, ComposerFile, Platform, Product, SourceSite, VideoAspect } from './Composer';
import { CHIPS, CHIPS_VIDEO, PLACEHOLDER_EXAMPLES, PLACEHOLDER_VIDEO, type Chip } from './landingData';
import { fmtBytes } from './format';

type Notify = (kind: 'FAILED', message: string) => void;

/**
 * State + actions of the Website / Video composer, shared by the landing hero and
 * the dashboard Home so both build sites and videos the same way:
 *  - Website: create the app, hand the prompt (plus attached media ids) to the studio
 *    via sessionStorage, full page load to /studio/:id (COOP/COEP).
 *  - Video: create the video project (format, source site, attachments) and open
 *    /video/:id with the first chat message.
 */
export function useComposer(opts: { isAuthenticated: boolean; notify: Notify; chipCount?: number }) {
    const { isAuthenticated, notify, chipCount = 5 } = opts;
    const navigate = useNavigate();
    const [prompt, setPrompt] = useState('');
    const [platform, setPlatform] = useState<Platform>('web');
    const [product, setProduct] = useState<Product>('web');
    const [aspect, setAspect] = useState<VideoAspect>('portrait');
    const [files, setFiles] = useState<ComposerFile[]>([]);
    const [attaching, setAttaching] = useState(false);
    const [source, setSource] = useState<SourceSite | null>(null);
    const [sourceSites, setSourceSites] = useState<SourceSite[]>([]);
    const [chipOffset, setChipOffset] = useState(0);
    const [placeholderIndex, setPlaceholderIndex] = useState(0);
    const [submitting, setSubmitting] = useState(false);
    // The design's launch overlay: up on the click, the studio keeps it until loaded.
    const [boot, setBoot] = useState<BootCopy | null>(null);
    const [bootKind, setBootKind] = useState<BootKind>('site');
    const launch = (copy: BootCopy, kind: BootKind = 'site') => { setBoot(copy); setBootKind(kind); startBoot(copy, kind); };
    const abortLaunch = () => { setBoot(null); clearBoot(); };

    useEffect(() => {
        const id = setInterval(() => setPlaceholderIndex((i) => i + 1), 3400);
        return () => clearInterval(id);
    }, []);

    // "From my site" (video): the signed-in user's sites.
    useEffect(() => {
        if (!isAuthenticated) { setSourceSites([]); return; }
        videoService.config()
            .then((c) => setSourceSites(c.apps.map((a) => ({ id: a.id, name: a.name, url: a.published ? 'Published' : 'Draft', thumb: a.thumbnailUrl }))))
            .catch(() => setSourceSites([]));
    }, [isAuthenticated]);

    const chips: Chip[] = useMemo(() => {
        const list = product === 'video' ? CHIPS_VIDEO : CHIPS;
        const o = chipOffset % list.length;
        return list.slice(o).concat(list.slice(0, o)).slice(0, chipCount);
    }, [chipOffset, product, chipCount]);
    const placeholders = product === 'video' ? PLACEHOLDER_VIDEO : PLACEHOLDER_EXAMPLES;

    const pickProduct = (p: Product) => { setProduct(p); setChipOffset(0); if (p === 'web') setSource(null); };

    // Attachments are uploaded to the media library right away.
    const attachFiles = async (list: FileList) => {
        if (!isAuthenticated) { navigate('/login'); return; }
        const picked = Array.from(list).filter((f) => f.type.startsWith('image/') || f.type.startsWith('video/'));
        if (picked.length < list.length) notify('FAILED', 'Hanya gambar dan video yang bisa dilampirkan.');
        if (!picked.length) return;
        setAttaching(true);
        for (const f of picked) {
            const res = await mediaService.upload(f);
            if (res.success && res.data) {
                const a = res.data;
                setFiles((prev) => [...prev, { id: a.assetId, name: a.originalName, meta: `${a.kind === 'video' ? 'Video' : 'Image'} · ${fmtBytes(a.bytes)}`, isImg: a.kind === 'image', url: a.url }]);
            } else {
                notify('FAILED', (res as any).message || `Upload ${f.name} gagal.`);
            }
        }
        setAttaching(false);
    };

    const submitVideo = async () => {
        if (submitting) return;
        if (!isAuthenticated) { navigate('/login'); return; }
        setSubmitting(true);
        launch('CREATING YOUR VIDEO', 'video');
        try {
            const { id } = await videoService.create({ format: aspect, applicationId: source?.id, assetIds: files.map((f) => f.id) });
            // Same first-message wording as the /video composer (VideoListView).
            const withFiles = files.length ? ' Pakai gambar & video yang saya lampirkan.' : '';
            const text = prompt.trim();
            const firstMessage = source
                ? `Buatkan video promosi dari situs saya.${withFiles}${text ? ` ${text}` : ''}`
                : text
                    ? `${text}${withFiles}`
                    : 'Buatkan video promosi dari gambar & video yang saya lampirkan.';
            navigate(`/video/${id}`, { state: { firstMessage } });
        } catch (e: any) {
            abortLaunch();
            notify('FAILED', e?.response?.data?.message || e?.message || 'Video gagal dibuat. Coba lagi.');
            setSubmitting(false);
        }
    };

    const submit = async () => {
        if (product === 'video') { void submitVideo(); return; }
        if (!prompt.trim() || submitting) return;
        if (!isAuthenticated) { navigate('/login'); return; }
        setSubmitting(true);
        launch('BUILDING YOUR SITE');
        // No name is sent: the server derives one from the prompt (AppNamerService).
        const response = await tenantService.createTenant({ description: prompt });
        if (response.success && response.data) {
            const appId = response.data.id;
            trackAppCreation('prompt');
            // Attached files ride along as their media ids, which the agent resolves
            // (AiAgentService.promptAssetBlock). The app's description stays the plain prompt.
            const studioPrompt = files.length ? `${prompt}\n\nGunakan file yang saya lampirkan: ${files.map((f) => f.id).join(', ')}` : prompt;
            sessionStorage.setItem(`hyvay:initial-prompt:${appId}`, studioPrompt);
            window.location.assign(`/studio/${appId}`);
            return;
        }
        abortLaunch();
        notify('FAILED', (response as any).message || 'Could not create the project. Please try again.');
        setSubmitting(false);
    };

    /** Props for <ComposerCard>, minus credits / top-up which the page owns. */
    const cardProps = (credits: number | null, onOpenTopUp: () => void): ComposerCardProps => ({
        prompt, onPrompt: setPrompt, onSubmit: () => void submit(), submitting, platform, onPlatform: setPlatform,
        credits, onOpenTopUp, placeholderIndex, placeholderText: placeholders[placeholderIndex % placeholders.length],
        product, onProduct: pickProduct, aspect, onAspect: setAspect, files, uploading: attaching,
        onPickFiles: (l) => void attachFiles(l), onRemoveFile: (id) => setFiles((prev) => prev.filter((f) => f.id !== id)),
        sourceSites, source, onSource: setSource,
    });

    return {
        prompt, setPrompt, product, pickProduct, setAspect, setSource, chips,
        shuffle: () => setChipOffset((o) => o + 3),
        fillChip: (c: Chip) => setPrompt(c.prompt),
        submit, cardProps, boot, bootKind, launch, abortLaunch,
    };
}
